import React from 'react';
import cn from 'classnames';
import { ChevronDown } from 'lucide-react';

type SharedProps = {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  showRequiredErrors?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  requiredMarkClassName?: string;
  dataTestId?: string;
  wrapperTestId?: string;
};

type TextNumberInputProps = SharedProps & {
  type: 'text' | 'number' | 'date';
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  inputClassName?: string;
  placeholder?: string;
};

type CheckboxInputProps = SharedProps & {
  type: 'checkbox';
  value: boolean;
  onChange: (checked: boolean) => void;
  inputClassName?: string;
};

type TextAreaInputProps = SharedProps & {
  type: 'textarea';
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  inputClassName?: string;
  placeholder?: string;
  rows?: number;
};

type SelectOption = { label: string; value: string; disabled?: boolean } | string;

type SelectInputProps = SharedProps & {
  type: 'select';
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  inputClassName?: string;
};

type RadioOption = {
  label: string;
  value: string;
};

type RadioInputProps = SharedProps & {
  type: 'radio';
  options: RadioOption[] | string[];
  value: string;
  onChange: (value: string) => void;
  groupWrapperClassName?: string;
  groupClassName?: string;
  radioItemClassName?: string;
  radioLabelClassName?: string;
};

type InputFieldProps = TextNumberInputProps | CheckboxInputProps | TextAreaInputProps | RadioInputProps | SelectInputProps;

const InputField: React.FC<InputFieldProps> = (props) => {
  const [touched, setTouched] = React.useState<boolean>(false);
  const {
    name,
    label,
    required = false,
    disabled = false,
    showRequiredErrors = false,
    wrapperClassName,
    labelClassName,
    requiredMarkClassName = 'text-red-600 text-xl',
    dataTestId,
    wrapperTestId,
  } = props as SharedProps;

  const errorId = `${name}-error`;

  const hasRequiredError = (value: string | boolean | undefined) => {
    if (!required || disabled || !(touched || showRequiredErrors)) return false;
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (typeof value === 'boolean') return value === false;
    return false;
  };

  const getRequiredFieldLabel = () => {
    const fieldLabel = label?.trim() || name?.trim() || 'This field';
    return fieldLabel.replace(/:\s*$/, '');
  };

  const renderRequiredError = (value: string | boolean | undefined) => {
    if (!hasRequiredError(value)) return null;
    return (
      <p id={errorId} className='mt-1 text-left text-sm text-red-600' role='alert'>
        {`${getRequiredFieldLabel()} is required`}
      </p>
    );
  };

  if (props.type === 'radio') {
    const { options, value, onChange, groupWrapperClassName, groupClassName, radioItemClassName, radioLabelClassName } = props as RadioInputProps;
    const hasError = hasRequiredError(value);

    return (
      <fieldset
        className={cn(
          wrapperClassName,
          hasError && 'rounded-md border border-red-600 !outline-red-600',
          'hover:outline-sky-800 hover:bg-blue-50 !rounded-md'
        )}
        onBlur={() => setTouched(true)}
      >
        <legend className={cn(labelClassName, hasError && 'text-red-600')}>
          {label}
          {required && (
            <span aria-hidden className={cn(requiredMarkClassName)}>
              {' '}
              *
            </span>
          )}
        </legend>
        <div className={cn(groupWrapperClassName)}>
          <div
            className={cn(groupClassName)}
            role='radiogroup'
            aria-required={required || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? errorId : undefined}
          >
            {options.map((option: RadioOption | string, index: number) => {
              const normalizedOption = typeof option === 'string' ? { label: option, value: option } : option;
              const inputId = `${name}-${index}`;
              return (
                <div key={(normalizedOption as any).value} className={cn(radioItemClassName)}>
                  <input
                    id={inputId}
                    type='radio'
                    name={name}
                    value={(normalizedOption as any).value}
                    checked={value === (normalizedOption as any).value}
                    onChange={(event) => onChange(event.target.value)}
                    required={required && index === 0}
                    disabled={disabled}
                    aria-checked={value === (normalizedOption as any).value}
                  />
                  <label htmlFor={inputId} className={cn(radioLabelClassName)}>
                    {(normalizedOption as any).label}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
        {renderRequiredError((props as RadioInputProps).value)}
      </fieldset>
    );
  }

  if (props.type === 'checkbox') {
    const { value, onChange, inputClassName } = props as CheckboxInputProps;
    const hasError = hasRequiredError(value);

    return (
      <div className={cn(wrapperClassName)}>
        <label className={cn(labelClassName, hasError && 'text-red-600')} htmlFor={name}>
          <input
            id={name}
            name={name}
            type='checkbox'
            checked={value}
            onChange={(event) => {
              setTouched(true);
              onChange(event.target.checked);
            }}
            disabled={disabled}
            aria-required={required || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? errorId : undefined}
            className={cn(inputClassName)}
            data-testid={dataTestId}
          />
          <span className='ml-2'>
            {label}
            {required && (
              <span aria-hidden className={cn(requiredMarkClassName)}>
                {' '}
                *
              </span>
            )}
          </span>
        </label>
        {renderRequiredError(value)}
      </div>
    );
  }

  if (props.type === 'textarea') {
    const { value, onChange, onBlur, inputClassName, placeholder = '', rows = 5 } = props as TextAreaInputProps;
    const hasError = hasRequiredError(value);

    return (
      <div className={cn(wrapperClassName, 'items-stretch')}>
        <div className='self-stretch pb-2 text-left'>
          <label className={cn(labelClassName, hasError && 'text-red-600')} htmlFor={name}>
            {label}
            {required && (
              <span aria-hidden className={cn(requiredMarkClassName)}>
                {' '}
                *
              </span>
            )}
          </label>
        </div>
        <textarea
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={(event) => {
            setTouched(true);
            onBlur?.(event);
          }}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          aria-required={required || undefined}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? errorId : undefined}
          className={cn(
            inputClassName,
            '!rounded-md placeholder:text-neutral-950/50 hover:outline-sky-800 hover:bg-blue-50',
            hasError && '!border-red-600 !outline-red-600 focus:!border-red-600 focus:!ring-red-200'
          )}
          rows={rows}
          data-testid={dataTestId}
        />
        {renderRequiredError(value)}
      </div>
    );
  }

  if (props.type === 'select') {
    const { options, value, onChange, inputClassName } = props as SelectInputProps;
    const hasError = hasRequiredError(value);
    const flatOptions = options.map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt));

    return (
      <div className={cn(wrapperClassName, 'relative')} data-testid={wrapperTestId}>
        <div className='self-stretch pb-2 text-left'>
          <label className={cn(labelClassName, hasError && 'text-red-600')} htmlFor={name}>
            {label}
            {required && (
              <span aria-hidden className={cn(requiredMarkClassName)}>
                {' '}
                *
              </span>
            )}
          </label>
        </div>
        <div className='relative w-full'>
          <select
            id={name}
            name={name}
            value={value || ''}
            onChange={(e) => {
              setTouched(true);
              onChange(e.target.value);
            }}
            required={required}
            disabled={disabled}
            aria-required={required || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? errorId : undefined}
            className={cn(
              'w-full h-11 px-3 py-2.5 pr-10 !rounded-md outline outline-1 outline-offset-[-1px] outline-brand-border text-base font-normal leading-6 bg-white text-gray-900',
              'appearance-none cursor-pointer',
              'disabled:bg-gray-100 disabled:text-gray-900 disabled:cursor-not-allowed disabled:opacity-100',
              'hover:bg-blue-50 hover:outline-sky-800 disabled:hover:bg-gray-100',
              'focus:outline-none focus:outline-2 focus:outline-offset-[-1px] focus:outline-dark-blue',
              inputClassName,
              hasError && '!outline-red-600'
            )}
            data-testid={wrapperTestId ? undefined : dataTestId}
          >
            {flatOptions.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={(opt as { disabled?: boolean }).disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown className='pointer-events-none absolute right-3 top-1/3 h-4 w-4 -translate-y-1/2 text-gray-900' />
        </div>
        {renderRequiredError(value)}
      </div>
    );
  }

  const textInputProps = props as TextNumberInputProps;
  const { type, value, onChange, onBlur, onFocus, inputClassName, placeholder = '' } = textInputProps;
  const hasError = hasRequiredError(value);

  return (
    <div className={cn(wrapperClassName)}>
      <div className='self-stretch pb-2 text-left'>
        <label className={cn(labelClassName, hasError && 'text-red-600')} htmlFor={name}>
          {label}
          {required && (
            <span aria-hidden className={cn(requiredMarkClassName)}>
              {' '}
              *
            </span>
          )}
        </label>
      </div>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={(event) => {
          setTouched(true);
          onBlur?.(event);
        }}
        onFocus={onFocus}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? errorId : undefined}
        className={cn(
          inputClassName,
          '!rounded-md border border-brand-border placeholder:text-neutral-950/50 outline-none focus:border-brand-border focus:ring-2 focus:ring-dark-blue focus:ring-offset-1 hover:outline-sky-800 hover:bg-blue-50',
          hasError && '!border-red-600 !outline !outline-1.5 !outline-red-600 focus:!border-red-600 focus:!ring-red-200'
        )}
        data-testid={dataTestId}
      />
      {renderRequiredError(value)}
    </div>
  );
};

const MemoInputField = React.memo(InputField);
MemoInputField.displayName = 'InputField';

export default MemoInputField;
